"use client";

import { Clock, CheckCircle2, Phone, MessageCircle } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function Problem() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const benefits = [{
    text: "זמינות מיידית תוך 5 שעות בלבד בכל אזור תל אביב"
  }, {
    text: "הגעה תוך 2-3 שעות במקרי חירום בכל רחבי העיר"
  }, {
    text: "מטפלים ישראלים מוסמכים, דוברי עברית, עם ניסיון מוכח"
  }, {
    text: "ליווי אישי והתאמה מדויקת בין המטופל למטפל"
  }, {
    text: "מטפלי לילה מנוסים להשגחה צמודה בבית ובבית החולים"
  }, {
    text: "שירות גמיש – החל משעות בודדות ועד השגחה של 24/7"
  }, {
    text: "מענה אנושי ומקצועי בכל שעות היממה"
  }];
  const faqs = [{
    question: "תוך כמה זמן ניתן לקבל מטפל סיעודי בתל אביב?",
    answer: "אנחנו מתחייבים לספק מטפל סיעודי מקצועי תוך עד 5 שעות מרגע הפנייה, בכל רחבי תל אביב והסביבה."
  }, {
    question: "האם המטפלים שלכם הם ישראלים?",
    answer: "כן, אנחנו עובדים אך ורק עם מטפלים ומטפלות ישראלים, דוברי עברית, שעברו סינון קפדני וראיונות עומק."
  }, {
    question: "האם ניתן להזמין מטפל סיעודי פרטי לבית החולים?",
    answer: "בוודאי. השירות שלנו כולל השגחה בבתי חולים (כמו איכילוב או אסותא) וכן טיפול והשגחה בבית המטופל."
  }, {
    question: "מה העלות של מטפל סיעודי בבית?",
    answer: "העלות משתנה בהתאם למורכבות הטיפול ומספר השעות הנדרשות. צרו איתנו קשר להצעת מחיר מדויקת ומותאמת אישית."
  }, {
    question: "האם אתם מספקים שירותי השגחת לילה בבית חולים איכילוב?",
    answer: "בהחלט. אנו מספקים שירותי מטפל סיעודי פרטי תל אביב להשגחה צמודה בבית החולים איכילוב, אסותא ומרכזים רפואיים נוספים בעיר. השירות כולל השגחת לילה מלאה או סיוע בשעות היום וליווי לאחר שחרור, לפי הצורך שלכם."
  }, {
    question: "האם השירות זמין גם בשבתות וחגים?",
    answer: "כן, מוקד השירות שלנו והמטפלים זמינים 24/7, כולל סופי שבוע וחגים, כדי לתת מענה לכל צורך דחוף שעולה."
  }, {
    question: "אילו סוגי הכשרות עוברים המטפלים שלכם?",
    answer: "המטפלים שלנו הם בעלי ניסיון עשיר בטיפול סיעודי, וחלקם הגדול בעלי תעודות הסמכה מקצועיות בתחום הסיעוד והעזרה הראשונה."
  }, {
    question: "מה קורה אם המטפל לא מתאים למטופל?",
    answer: "אנחנו שמים דגש רב על ההתאמה האישית. אם מכל סיבה שהיא המטפל אינו מתאים, אנו נדאג להחלפה מהירה במטפל אחר שיתאים טוב יותר לצרכים שלכם."
  }];
  return <section id="בעיה" className="relative bg-slate-50 py-[120px] overflow-hidden flex items-center justify-center">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0 overflow-hidden">
        <Clock className="w-[600px] h-[600px] md:w-[800px] md:h-[800px] text-emerald-500 opacity-5" strokeWidth={0.5} aria-hidden="true" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 relative z-10 flex flex-col items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-12 text-center font-default leading-tight max-w-[1000px]">
          מטפל סיעודי בתל אביב: פתרון מקצועי ומיידי לכל צורך סיעודי
        </h1>

        <div className="text-center mb-12">
          <span className="text-emerald-600 font-bold tracking-wider uppercase text-sm mb-4 block">
            פתרון מיידי לצרכי סיעוד
          </span>
          <h2 className="text-4xl md:text-[40px] font-bold text-slate-900 mb-4 font-default leading-tight">
            התמודדות עם צורך פתאומי במטפל סיעודי בתל אביב
          </h2>
          <p className="text-xl text-emerald-800 font-bold max-w-[800px] mx-auto leading-relaxed mb-6">
            החיפוש אחר <strong>מטפל סיעודי תל אביב</strong> יכול להיות מלחיץ ומבלבל, במיוחד כשצריך פתרון מעכשיו לעכשיו. ב-Siudy, אנחנו מבינים את הדחיפות ומציעים לכם <strong>מטפל סיעודי פרטי תל אביב</strong> המגיע אליכם הביתה או למחלקה בבית החולים בתוך זמן קצר במיוחד, כדי שתוכלו להתרכז בהחלמה ובשקט המשפחתי.
          </p>
          <p className="text-lg text-slate-600 max-w-[800px] mx-auto leading-relaxed">
            כשמתעורר צורך דחוף בעזרה סיעודית, במיוחד בעיר תוססת ודינמית כמו תל אביב, המשפחה מוצאת את עצמה פעמים רבות תחת לחץ עצום. הצורך למצוא <Link to="#" className="text-emerald-600 hover:underline font-bold mx-1">מטפל סיעודי בתל אביב</Link> אמין ומקצועי בזמן קצר הוא אתגר משמעותי שדורש פתרון מהיר ללא פשרות על איכות הטיפול. אנו גאים להציע שירותי <Link to="#" className="text-emerald-600 hover:underline font-bold mx-1">מטפל סיעודי בבית</Link> לכל צורך.
          </p>
        </div>

        <h3 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-8 font-default max-w-[900px]">
          הזמן אוזל כשצריך למצוא <Link to="#" className="text-emerald-600 hover:underline font-bold mx-1">מטפל סיעודי בתל אביב</Link> – אנחנו כאן כדי לעצור את השעון
        </h3>

        <div className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl border border-slate-100 shadow-sm mb-12 max-w-[850px] text-center">
          <h4 className="text-2xl font-bold text-emerald-700 mb-4">
            הפתרון שלנו: מטפל סיעודי פרטי בתוך שעות ספורות
          </h4>
          <p className="text-xl text-slate-700 leading-relaxed">
            אנחנו ב-Siudy מבינים שסיעוד הוא לא משהו שאפשר להמתין לו ימים ארוכים. המערך המבצעי שלנו בתל אביב מאפשר לכם לקבל מטפל סיעודי בבית או <Link to="#" className="text-emerald-600 hover:underline font-bold mx-1">השגחה בבית חולים</Link> תוך פחות מ-5 שעות מרגע יצירת הקשר. אנחנו מסירים מכם את נטל החיפוש ומבטיחים לכם שקט נפשי עם מטפלים ישראלים מנוסים.
          </p>
        </div>
        
        <div className="text-right w-full max-w-[1000px] space-y-8 text-slate-700 font-default leading-loose">
          <p>
            חיפוש אחר <strong>מטפל סיעודי</strong> בתל אביב יכול להיות משימה מתישה ומורכבת, במיוחד כאשר מדובר במצב חירום רפואי או בהידרדרות פתאומית במצבו של אדם יקר. המשפחה נדרשת לקבל החלטות הרות גורל בתוך דקות, תוך שהיא מנסה לנווט בין חברות כוח אדם, בירוקרטיה של ביטוח לאומי וחיפוש פרטי ברשתות החברתיות. במקרים רבים, הזמן הוא המשאב היקר ביותר, והצורך בשירותי <strong>מטפל סיעודי פרטי</strong> הופך לקריטי כדי להבטיח השגחה רציפה ובטוחה. <Link to="#" className="text-emerald-600 hover:underline font-bold mx-1">למידע נוסף על השירותים שלנו</Link>
          </p>
          <p>
            כאשר אנו מדברים על <strong>מטפל סיעודי בבית</strong>, אנו לא מדברים רק על סיוע פיזי, אלא על מעטפת רגשית ומקצועית המאפשרת למטופל להישאר בסביבתו המוכרת והתומכת. תל אביב, כעיר ללא הפסקה, מציבה אתגרים ייחודיים כמו פקקי תנועה, קשיי חניה וקצב חיים מהיר, המקשים על מטפלים רבים להגיע בזמן. לכן, השירות שלנו מתמקד בפריסה רחבה של מטפלים הגרים ועובדים בעיר, מה שמבטיח הגעה מהירה לכל שכונה – מצפון תל אביב ועד יפו, כולל זמינות מיידית למחלקות האשפוז בבית החולים <strong>איכילוב</strong>. <Link to="#" className="text-emerald-600 hover:underline font-bold mx-1">גלו מי אנחנו</Link>
          </p>
          <p>
            הבחירה בחיפוש <strong>מטפל סיעודי</strong> דרך חברה מקצועית המציעה זמינות מיידית היא הדרך היחידה להבטיח שלא תתפשרו על איכות הטיפול בגלל הלחץ. מטפל מקצועי יודע להתמודד עם מצבים רפואיים מורכבים, לנהל את סדר היום של המטופל ולהעניק למשפחה את המרווח הדרוש לה כדי להתארגן לטווח הארוך. השירות של <strong>מטפל סיעודי פרטי תל אביב</strong> מעניק גמישות מלאה – החל מהשגחת לילה בבית או בבית חולים ועד לטיפול אינטנסיבי מסביב לשעון, בהתאם לצרכים המשתנים של יקירכם. אנו מתמחים בליווי לאחר ניתוחים מורכבים ובמתן מענה סיעודי מקצועי המותאם לקצב של <strong>מטפל סיעודי תל אביב</strong>.
          </p>
          <p>
            רבים שואלים את עצמם מדוע כדאי להעדיף <Link to="/" className="text-emerald-600 hover:underline font-bold mx-1">מטפל סיעודי בבית</Link> על פני פתרונות אחרים. התשובה טמונה באיכות החיים של המטופל. בבית, המטופל מוקף בזכרונותיו, בחפציו ובמשפחתו, מה שתורם משמעותית למצבו המורלי והבריאותי. נוכחות של <strong>מטפל סיעודי</strong> קבוע ומיומן מפחיתה את הסיכון לנפילות, טעויות בנטילת תרופות ותחושת בדידות, שהן הבעיות המרכזיות בגיל השלישי או בתקופת שיקום.
          </p>
          <p>
            בתהליך מציאת <strong>מטפל סיעודי פרטי</strong>, אנו מקפידים על סינון קפדני. לא כל אחד יכול להיות מטפל; נדרשת סבלנות אין קץ, חמלה וידע מקצועי רחב. המטפלים שלנו עוברים הכשרות ומתמחים בעבודה מול מטופלים במצבים שונים, כולל דמנציה, שיקום לאחר ניתוח או מחלות כרוניות. המטרה שלנו היא שכל <strong>מטפל סיעודי</strong> שיוצא מטעמנו יהיה לא רק איש מקצוע, אלא גם בן אדם שהייתם סומכים עליו בעיניים עצומות.
          </p>
          <p>
            היתרון הגדול ביותר בשימוש בשירותי <strong>מטפל סיעודי בבית</strong> דרך פלטפורמה כמו Siudy הוא המהירות. בעוד שחברות סיעוד מסורתיות עשויות לקחת ימים עד לשיבוץ מטפל, אנו מבינים את הדחיפות. תל אביב דורשת קצב אחר, ואנו מספקים אותו. בין אם אתם זקוקים לליווי לאחר שחרור מבית החולים או לעזרה פתאומית בגלל היעדרות של המטפל הקבוע, אנו נדאג שיהיה אצלכם <Link to="#" className="text-emerald-600 hover:underline font-bold mx-1">מטפל סיעודי בתל אביב</Link> איכותי בתוך שעות ספורות בלבד.
          </p>
          <p>
            לסיכום, הצורך בחיפוש <strong>מטפל סיעודי פרטי</strong> הוא רגע מאתגר לכל משפחה. אל תתנו ללחץ להוביל אתכם לבחירות לא נכונות. עם הליווי המקצועי שלנו, תוכלו להבטיח שיקירכם יקבל את הטיפול הטוב ביותר בביתם. <strong>מטפל סיעודי בבית</strong> הוא לא רק פתרון טכני, הוא הבטחה לביטחון, כבוד ואיכות חיים גם ברגעים הקשים ביותר. צרו איתנו קשר עכשיו ונחבר אתכם למטפל המתאים ביותר בתל אביב עוד היום. <Link to="#" className="text-emerald-600 hover:underline font-bold mx-1">צרו קשר לייעוץ ראשוני</Link>
          </p>
        </div>

        <div className="w-full max-w-[900px] mt-16 text-right">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 border-r-4 border-emerald-500 pr-4">
            היתרונות של השירות שלנו בתל אביב
          </h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0 mt-1" />
                <span className="text-lg text-slate-700">{benefit.text}</span>
              </li>)}
          </ul>
        </div>

        <div className="w-full max-w-[900px] mt-16 text-right">
          <h2 className="text-3xl font-bold text-slate-900 mb-6 border-r-4 border-emerald-500 pr-4">
            מידע מורחב על שירותי סיעוד בתל אביב
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed mb-6">
            שירותי הסיעוד בתל אביב כוללים מגוון רחב של פתרונות המותאמים אישית לצרכי הקשיש או המטופל. אנו מספקים מענה לשכונות כמו רמת אביב, צהלה, מרכז העיר, פלורנטין ויפו. השירות כולל סיוע בפעולות יומיומיות (ADL), ניהול משק בית קל, ליווי לבדיקות רפואיות, והכי חשוב – חברה אנושית ותמיכה רגשית. המטפלים שלנו מנוסים בעבודה עם קופות החולים השונות ועם בתי החולים הגדולים באזור, מה שמאפשר רצף טיפולי איכותי מרגע האשפוז ועד לחזרה הביתה.
          </p>
        </div>

        <div className="w-full max-w-[800px] mt-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            שאלות ותשובות נפוצות
          </h2>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`faq-${i}`} data-index={i} className="bg-white border border-slate-200 rounded-xl px-4 overflow-hidden">
                <AccordionTrigger className="text-lg font-medium text-slate-800 hover:no-underline py-4 text-right">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 text-base leading-relaxed pb-4 text-right">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        <Card className="w-full max-w-[800px] mt-20 bg-emerald-900 text-white border-none shadow-xl overflow-hidden">
          <CardContent className="p-10 flex flex-col items-center text-center">
            <h2 className="text-3xl font-bold mb-4">אל תתפשרו על הטיפול הטוב ביותר</h2>
            <p className="text-emerald-100 text-lg mb-8 max-w-[600px]">
              הזמינו <strong>מטפל סיעודי תל אביב</strong> או <strong>מטפל סיעודי פרטי תל אביב</strong> עכשיו והבטיחו ליקירכם השגחה מקצועית ומהירה בבית או בבית החולים (איכילוב, אסותא ועוד).
            </p>
            <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
              <Button asChild size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6 text-xl h-auto">
                <Link to="tel:050-000-0000" className="flex items-center gap-2">
                  <Phone className="w-6 h-6" />
                  התקשרו עכשיו
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-emerald-400 text-emerald-400 hover:bg-emerald-400/10 rounded-full px-8 py-6 text-xl h-auto">
                <Link to="https://wa.me/972500000000" newTab className="flex items-center gap-2">
                  <MessageCircle className="w-6 h-6" />
                  וואטסאפ מיידי
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-12 flex flex-wrap justify-center gap-6">
          <Link to="/" className="text-slate-500 hover:text-emerald-600 font-medium transition-colors">דף הבית</Link>
          <Link to="#" className="text-slate-500 hover:text-emerald-600 font-medium transition-colors">אזורים נוספים</Link>
        </div>
      </div>
    </section>;
}